import { defineComponent as _defineComponent } from 'vue'
import { UpcomingRelease } from '@/@types/graphql-types';
import { getExcludedPackageShortNames } from '@/constants/package-exclusion';
import { BuyBoxOffers, useBuyBoxData } from '@/features/buybox/composables/useBuyboxData';
import {
	GetBuyBoxOffersDocument,
	GetBuyBoxOffersQueryVariables,
	type GetBuyBoxOffersQuery,
} from '@/features/buybox/graphql/queries/GetBuyBoxOffers.query';
import { buyboxFilterByMonetizationType } from '@/features/buybox/helpers/list-buybox-config';
import { useInternationalOffersStatus } from '@/features/international-offers/composables/useInternationalOffers';
import { useInternationalOffersTracking } from '@/features/international-offers/tracking';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import { useQuery } from '@/helpers/composables/useApollo';
import { mrcObservableOptions } from '@/helpers/composables/useBatchImpressionTracking';
import { useI18n } from '@/helpers/composables/useI18n';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { IN_LISTS_KEY, useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { useTitleWatchlist } from '@/helpers/composables/useTitleWatchlist';
import { timestamp } from '@/helpers/date-helper';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { SnowplowTitleContextGraphql, type SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { BuyBoxFilterOptions, NewBuyBoxView } from '@/stores/useUserStoreTypes';
import type { EpisodeFragment } from '@/pages/graphql/fragments/Episode.fragment';
import { computed, inject, provide, ref, watch } from 'vue';

// need to import this immediately because it's above the fold and could cause CLS
import { useDoubleStackExp } from '@/components/experiments/DoubleStackExp';
import BuyBoxCountrySwitcher from '@/features/buybox/components/BuyBoxCountrySwitcher.vue';
import BuyBoxTypeSwitcher from '@/features/buybox/components/BuyBoxTypeSwitcher.vue';
import ListBuyBoxFilter from '@/features/buybox/components/list/ListBuyBoxFilter.vue';
import ListUpcomingNoOffers from '@/features/buybox/components/list/ListUpcomingNoOffers.vue';
import PromotedListBuyBox from '@/features/buybox/components/list/PromotedListBuyBox.vue';
import RegularListBuyBox from '@/features/buybox/components/list/RegularListBuyBox.vue';
import { useActivateExperiment, useExperiment } from '@/libraries/experiment';
import { useTitleDetailInfo } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'ListBuyBox',
  props: {
    title: null,
    episode: null,
    buyboxOffers: { default: undefined },
    sponsoredAd: { default: undefined },
    buyBoxSwitchType: null,
    hideBuyBoxSwitcher: { type: Boolean, default: false },
    additionalContexts: { default: () => [] },
    episodeNumber: null,
    placement: { default: 'regular_buybox' },
    upcomingEpisodeReleases: { default: null }
  },
  emits: {
	onWatchlistToggle: (createdAt: string | null) => ({ createdAt }),
},
  setup(__props: any, { emit: emits }) {

const props = __props as { title: TitleDetail, episode?: EpisodeFragment, buyboxOffers: BuyBoxOffers | null, sponsoredAd: SponsoredAdFragment | undefined, buyBoxSwitchType?: NewBuyBoxView, hideBuyBoxSwitcher: boolean, additionalContexts: SnowplowContext[], episodeNumber?: number | null, placement: ClickoutPlacement, upcomingEpisodeReleases: UpcomingRelease[] | null };

const OfferReporter = () => import('@/features/buybox/components/OfferReporter.vue');
const PlexPlayerModal = () => import('@/ui-components/video-player-modals/PlexPlayerModal.vue');
const BuyboxInternationalOffers = () =>
	import('@/features/international-offers/components/BuyboxInternationalOffers.vue');




const { setInWatchlist, onIsLoggedInChange } = useTitleWatchlist(props.sponsoredAd);

const userStore = useUserStore();
const { setNewBuyboxFilterType, setBuyboxFilterTypeOptimistic } = userStore;
const { newBuyboxFilterType, buyboxFilterTypeOptimistic, isLoggedIn, isProUser } = storeToRefs(userStore);

const { country, language } = storeToRefs(useLanguageStore());
const { titleProviderCount } = storeToRefs(useConstantStore());
const { t } = useI18n();
const { trackInternationalOffers } = useInternationalOffersTracking({ title: () => props.title });

useProvideClickoutPlacement(props.placement);

const { titleId, titleName, titleObjectId, titleObjectType, isTitleUpcoming, seasonNumber, isInWatchList } =
	useTitleDetailInfo(() => props.title);

const buyBoxTitleId = computed(() => (props.episode ? props.episode.id : titleId.value));

const queryVariables = computed((): GetBuyBoxOffersQueryVariables => {
	return {
		nodeId: buyBoxTitleId.value ?? '',
		country: country.value,
		language: language.value,
		excludePackages: getExcludedPackageShortNames(
			isLoggedIn.value,
			buyBoxTitleId.value,
			country.value,
			isProUser.value
		),
		fallbackToForeignOffers: true,
	};
});

const { result, loading } = useQuery<GetBuyBoxOffersQuery, GetBuyBoxOffersQueryVariables>(
	GetBuyBoxOffersDocument,
	queryVariables,
	() => ({ enabled: !props.buyboxOffers })
);

const buyboxData = computed(() => (!props.buyboxOffers ? (result.value?.node as BuyBoxOffers) : props.buyboxOffers));

const isEpisodeBuybox = computed(() => buyboxData.value?.__typename === 'Episode');

// episode buybox filter is not currently being stored. keep state for each Episode buybox at buybox level
const episodeBuyBoxType = ref<BuyBoxFilterOptions>(BuyBoxFilterOptions.ALL);
const episodeBuyBoxTypeOptimistic = ref<BuyBoxFilterOptions>(BuyBoxFilterOptions.ALL);

const buyboxFilterOptimistic = computed(() => {
	if (isEpisodeBuybox.value) {
		return episodeBuyBoxType.value;
	}
	return buyboxFilterTypeOptimistic.value ?? newBuyboxFilterType.value;
});
const buyboxFilter = computed(() => (isEpisodeBuybox.value ? episodeBuyBoxType.value : newBuyboxFilterType.value));

const buyboxUpcomingReleases = computed(() => {
	return props.upcomingEpisodeReleases ?? props.title.content.upcomingReleases;
});

const {
	filteredRegularOffers,
	allRegularOffers,
	upcomingReleases,
	upcomingReleasesFromGroups,
	promotedOffers,
	AVODOffer,
	dvdBluRayOffers,
	hasVODOffers,
} = useBuyBoxData({
	title: () => props.title,
	buyboxData,
	buyboxFilter,
	buyboxUpcomingReleases,
});

const buyboxContainer = ref<HTMLElement | null>(null);

// If the buybox is in the episodes list, use the episode id, objectId, objectType etc. instead of the title data
const buyBoxObjectId = computed(() => (props.episode ? props.episode.objectId : titleObjectId.value));
const buyBoxObjectType = computed(() => (props.episode ? props.episode.objectType : titleObjectType.value));
const buyBoxIsUpcoming = computed(() => (props.episode ? !props.episode.content.isReleased : isTitleUpcoming.value));
const buyBoxSeasonNumber = computed(() => (props.episode ? props.episode.content.seasonNumber : seasonNumber.value));

const { isActive: isInternationalOffersActive, isInternationalOffersTitle } = useInternationalOffersStatus({
	loading,
	title: () => props.title,
});

const buyBoxFilterOptions = computed(() => {
	const allOffers = allRegularOffers.value.concat(dvdBluRayOffers.value);
	return Object.keys(buyboxFilterByMonetizationType).filter(monetizationType => {
		// Only show Disc tab if there are physical offers present
		if (monetizationType === BuyBoxFilterOptions.PHYSICAL) {
			return dvdBluRayOffers.value.length > 0;
		}
		// Only show Buy/Rent tabs if there are non-physical Buy/Rent offers
		const offersToFilter =
			monetizationType === BuyBoxFilterOptions.BUY || monetizationType === BuyBoxFilterOptions.RENT
				? allRegularOffers.value
				: allOffers;

		return offersToFilter.some(offer =>
			buyboxFilterByMonetizationType[monetizationType as keyof typeof buyboxFilterByMonetizationType].includes(
				offer.monetizationType
			)
		);
	}) as BuyBoxFilterOptions[];
});

const titleContext = computed(() => {
	const title = props.title.__typename === 'Season' ? props.title.show : props.title;
	return new SnowplowTitleContextGraphql(
		title.objectId,
		title.objectType,
		buyBoxSeasonNumber.value ?? null,
		props.episodeNumber ?? null
	);
});

provide('titleContext', titleContext);
provide('titleSourceId', buyBoxTitleId.value);
provide('filterOption', buyboxFilter);

const { inPromotionCatalogue, promotionOffer } = usePromotion({ title: props.title, offers: allRegularOffers });

// DOUBLE_STACK_EXP
const { experimentInfo } = useExperiment('DOUBLE_STACK_EXP_3');
const isSessionVariant1 = computed(() => experimentInfo.value?.variant === 'variant_1');

const inLists = inject(
	IN_LISTS_KEY,
	computed(() => [])
);

const { isDoubleStackProvider } = useDoubleStackExp();
const activateDoubleStackExp = computed(() => {
	if (!promotionOffer.value || !isDoubleStackProvider(promotionOffer.value?.package.shortName)) return false;

	if (isSessionVariant1.value && !inLists.value.includes('EXP_PRIME_ORIGINALS')) return false;

	return inPromotionCatalogue.value;
});
useActivateExperiment('DOUBLE_STACK_EXP_3', activateDoubleStackExp);
// DOUBLE_STACK_EXP

const lastUpdated = computed(() => {
	if (
		buyboxData.value?.__typename === 'Episode' ||
		buyboxData.value?.__typename === 'Movie' ||
		buyboxData.value?.__typename === 'Season' ||
		buyboxData.value?.__typename === 'Show'
	) {
		return buyboxData.value.maxOfferUpdatedAt;
	}
	return null;
});

const timestampText = computed(() => {
	const updated = timestamp(
		lastUpdated.value,
		{ year: 'numeric', month: 'long', day: 'numeric' },
		country.value,
		language.value
	);
	return t('WEBAPP_BUYBOX_UPDATED_TIMESTAMP', {
		services_total: titleProviderCount.value,
		date: updated.date,
		time: updated.time,
	});
});

async function onNotifyMe() {
	if (props.title && buyBoxTitleId.value) {
		const title = props.title.__typename === 'Season' ? props.title.show : props.title;

		const updatedTitle = await setInWatchlist(
			title.id,
			isInWatchList.value,
			{
				title: title.content.title,
				objectType: title.objectType,
				objectId: title.objectId,
			},
			{
				source: 'buybox',
			}
		);

		if (updatedTitle) {
			emits('onWatchlistToggle', updatedTitle.data?.setInWatchlistV2.title.watchlistEntryV2?.createdAt);
		}
	}
}

function trackInternationalOffersImpression(isVisible: boolean) {
	if (isVisible) {
		trackInternationalOffers('impression');
	}
}

watch(
	isLoggedIn,
	newValue => {
		if (!newValue) return;
		if (props.title && buyBoxTitleId.value) {
			onIsLoggedInChange(
				buyBoxTitleId.value || '',
				{
					title: props.title?.content.title,
					objectType: props.title?.objectType,
					objectId: props.title?.objectId,
				},
				isInWatchList.value,
				'buybox'
			);
		}
	},
	{ immediate: true }
);

async function handleFilterClick(payload: { value: BuyBoxFilterOptions; nonInteraction?: boolean }) {
	const optimisticUpdate = () => {
		if (isEpisodeBuybox.value) {
			episodeBuyBoxTypeOptimistic.value = payload.value;
		} else {
			setBuyboxFilterTypeOptimistic(payload.value);
		}
	};

	const commitUpdate = () => {
		if (isEpisodeBuybox.value) {
			episodeBuyBoxType.value = payload.value;
		} else {
			setNewBuyboxFilterType(payload.value);
		}
	};

	// updating the buybox is expensive.
	// that's why we split it up in 1) optimistic UI update
	optimisticUpdate();
	// then 2) give the JS thread time to render the optimistic UI update immediately
	await new Promise(resolve => setTimeout(resolve, 0));
	// and 3) defer the expensive store commit to the end
	commitUpdate();

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'filter_toggled',
			label: 'buybox',
			property: payload.value,
			nonInteraction: payload.nonInteraction,
		},
		props.additionalContexts
	);
}

function watchNowTitleClicked() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'click', label: 'watch_now_headline_clicked' },
		props.additionalContexts
	);

	if (!AVODOffer.value) {
		const element = buyboxContainer.value?.querySelector('#regular-buybox');
		if (element) {
			element.scrollIntoView({ behavior: 'smooth' });
		}
	} else {
		ModalHelper.openModal(
			PlexPlayerModal,
			{ offer: AVODOffer.value, title: props.title },
			{ cssClass: 'trailer-modal' },
			'',
			true,
			[]
		);
	}
}

return { __sfc: true,OfferReporter, PlexPlayerModal, BuyboxInternationalOffers, emits, props, setInWatchlist, onIsLoggedInChange, userStore, setNewBuyboxFilterType, setBuyboxFilterTypeOptimistic, newBuyboxFilterType, buyboxFilterTypeOptimistic, isLoggedIn, isProUser, country, language, titleProviderCount, t, trackInternationalOffers, titleId, titleName, titleObjectId, titleObjectType, isTitleUpcoming, seasonNumber, isInWatchList, buyBoxTitleId, queryVariables, result, loading, buyboxData, isEpisodeBuybox, episodeBuyBoxType, episodeBuyBoxTypeOptimistic, buyboxFilterOptimistic, buyboxFilter, buyboxUpcomingReleases, filteredRegularOffers, allRegularOffers, upcomingReleases, upcomingReleasesFromGroups, promotedOffers, AVODOffer, dvdBluRayOffers, hasVODOffers, buyboxContainer, buyBoxObjectId, buyBoxObjectType, buyBoxIsUpcoming, buyBoxSeasonNumber, isInternationalOffersActive, isInternationalOffersTitle, buyBoxFilterOptions, titleContext, inPromotionCatalogue, promotionOffer, experimentInfo, isSessionVariant1, inLists, isDoubleStackProvider, activateDoubleStackExp, lastUpdated, timestampText, onNotifyMe, trackInternationalOffersImpression, handleFilterClick, watchNowTitleClicked, mrcObservableOptions, BuyBoxCountrySwitcher, BuyBoxTypeSwitcher, ListBuyBoxFilter, ListUpcomingNoOffers, PromotedListBuyBox, RegularListBuyBox }
}

})